import { create } from 'zustand';
import type { MapType, MassStatus, TimeStatus } from '../types';
import { apiUrl } from '../lib/config';

// API response types
interface MapSummary {
  id: string;
  name: string;
  type: MapType;
  role: string;
  systemCount: number;
  createdAt: number;
}

interface MapDetails {
  id: string;
  name: string;
  type: MapType;
  role: string;
  settings: Record<string, unknown> | null;
  createdAt: number;
}

interface SystemData {
  id: string;
  systemId: number;
  systemName: string;
  alias: string | null;
  positionX: number;
  positionY: number;
  locked: boolean;
  securityStatus: number | null;
  wormholeClass: string | null;
  effect: string | null;
  statics: string[] | null;
  addedBy: number;
  createdAt: number;
}

interface ConnectionData {
  id: string;
  sourceSystemId: string;
  targetSystemId: string;
  wormholeType: string | null;
  massStatus: MassStatus;
  timeStatus: TimeStatus;
  sourceSig: string | null;
  targetSig: string | null;
  createdAt: number;
  eolAt: number | null;
}

interface SignatureData {
  id: string;
  systemId: string;
  sigId: string;
  groupType: string;
  name: string | null;
  connectionId: string | null;
  createdBy: number;
  createdAt: number;
}

// Character presence for real-time tracking
interface CharacterPresence {
  characterId: number;
  characterName: string;
  systemId?: number;
  shipTypeId?: number;
  shipTypeName?: string;
  isOnline: boolean;
  lastSeen: number;
}

// WebSocket sync payload
interface SyncPayload {
  systems?: unknown[];
  connections?: unknown[];
  signatures?: unknown[];
  presence?: CharacterPresence[];
  lastUpdated?: number;
}

interface MapState {
  // Map list
  maps: MapSummary[];
  mapsLoading: boolean;
  mapsError: string | null;

  // Current map
  currentMap: MapDetails | null;
  systems: SystemData[];
  connections: ConnectionData[];
  signatures: SignatureData[];
  mapLoading: boolean;
  mapError: string | null;

  // Real-time presence
  presence: CharacterPresence[];

  // Selected items
  selectedSystemId: string | null;
  selectedConnectionId: string | null;

  // Actions - Map list
  fetchMaps: () => Promise<void>;
  createMap: (name: string, type: MapType) => Promise<string | null>;
  deleteMap: (mapId: string) => Promise<boolean>;

  // Actions - Current map
  loadMap: (mapId: string) => Promise<void>;
  unloadMap: () => void;

  // Actions - Systems
  addSystem: (systemId: number, alias?: string, positionX?: number, positionY?: number) => Promise<SystemData | null>;
  updateSystem: (systemRowId: string, updates: { alias?: string; positionX?: number; positionY?: number; locked?: boolean }) => Promise<boolean>;
  removeSystem: (systemRowId: string) => Promise<boolean>;

  // Actions - Connections
  addConnection: (sourceSystemId: string, targetSystemId: string, wormholeType?: string, massStatus?: MassStatus, timeStatus?: TimeStatus) => Promise<ConnectionData | null>;
  updateConnection: (connectionId: string, updates: { wormholeType?: string; massStatus?: MassStatus; timeStatus?: TimeStatus; eolAt?: number | null }) => Promise<boolean>;
  removeConnection: (connectionId: string) => Promise<boolean>;

  // Actions - Signatures
  importSignatures: (systemId: string, rawText: string, lazyDelete?: boolean) => Promise<{ added: string[]; updated: string[]; deleted: string[] } | null>;
  updateSignature: (signatureId: string, updates: { name?: string; connectionId?: string | null }) => Promise<boolean>;
  removeSignature: (signatureId: string) => Promise<boolean>;

  // Actions - Selection
  selectSystem: (systemId: string | null) => void;
  selectConnection: (connectionId: string | null) => void;

  // Actions - Optimistic updates (for WebSocket sync)
  applySystemUpdate: (system: SystemData) => void;
  applyConnectionUpdate: (connection: ConnectionData) => void;
  applySignatureUpdate: (signature: SignatureData) => void;
  applySystemRemoval: (systemId: string) => void;
  applyConnectionRemoval: (connectionId: string) => void;
  applySignatureRemoval: (signatureId: string) => void;

  // Actions - WebSocket sync
  setMapState: (payload: SyncPayload) => void;
  applyUpdate: (action: string, data: unknown) => void;
  confirmUpdate: (action: string, data: unknown) => void;
  setPresence: (presence: CharacterPresence[]) => void;
}

export const useMapStore = create<MapState>()((set, get) => ({
  // Initial state
  maps: [],
  mapsLoading: false,
  mapsError: null,

  currentMap: null,
  systems: [],
  connections: [],
  signatures: [],
  mapLoading: false,
  mapError: null,

  presence: [],

  selectedSystemId: null,
  selectedConnectionId: null,

  // Map list actions
  fetchMaps: async () => {
    set({ mapsLoading: true, mapsError: null });
    try {
      const response = await fetch(apiUrl('/api/maps'), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch maps');
      const data = await response.json();
      set({ maps: data.maps, mapsLoading: false });
    } catch (error) {
      set({ mapsError: error instanceof Error ? error.message : 'Unknown error', mapsLoading: false });
    }
  },

  createMap: async (name, type) => {
    try {
      const response = await fetch(apiUrl('/api/maps'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, type }),
        credentials: 'include',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create map');
      }
      const data = await response.json();
      const newMap: MapSummary = {
        id: data.map.id,
        name: data.map.name,
        type: data.map.type,
        role: 'owner',
        systemCount: 0,
        createdAt: data.map.createdAt,
      };
      set((state) => ({ maps: [newMap, ...state.maps] }));
      return data.map.id;
    } catch (error) {
      console.error('Failed to create map:', error);
      return null;
    }
  },

  deleteMap: async (mapId) => {
    try {
      const response = await fetch(apiUrl(`/api/maps/${mapId}`), { method: 'DELETE', credentials: 'include' });
      if (!response.ok) throw new Error('Failed to delete map');
      set((state) => ({
        maps: state.maps.filter((m) => m.id !== mapId),
        currentMap: state.currentMap?.id === mapId ? null : state.currentMap,
      }));
      return true;
    } catch (error) {
      console.error('Failed to delete map:', error);
      return false;
    }
  },

  // Current map actions
  loadMap: async (mapId) => {
    set({ mapLoading: true, mapError: null });
    try {
      const response = await fetch(apiUrl(`/api/maps/${mapId}`), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to load map');
      const data = await response.json();
      set({
        currentMap: data.map,
        systems: data.systems,
        connections: data.connections,
        signatures: data.signatures,
        mapLoading: false,
        selectedSystemId: null,
        selectedConnectionId: null,
      });
    } catch (error) {
      set({ mapError: error instanceof Error ? error.message : 'Unknown error', mapLoading: false });
    }
  },

  unloadMap: () => {
    set({
      currentMap: null,
      systems: [],
      connections: [],
      signatures: [],
      selectedSystemId: null,
      selectedConnectionId: null,
    });
  },

  // System actions
  addSystem: async (systemId, alias, positionX, positionY) => {
    const { currentMap } = get();
    if (!currentMap) return null;

    try {
      const response = await fetch(apiUrl(`/api/maps/${currentMap.id}/systems`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ systemId, alias, positionX, positionY }),
        credentials: 'include',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add system');
      }
      const data = await response.json();
      set((state) => ({ systems: [...state.systems, data.system] }));
      return data.system;
    } catch (error) {
      console.error('Failed to add system:', error);
      return null;
    }
  },

  updateSystem: async (systemRowId, updates) => {
    const { currentMap } = get();
    if (!currentMap) return false;

    try {
      const response = await fetch(apiUrl(`/api/maps/${currentMap.id}/systems/${systemRowId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to update system');

      set((state) => ({
        systems: state.systems.map((s) =>
          s.id === systemRowId ? { ...s, ...updates } : s
        ),
      }));
      return true;
    } catch (error) {
      console.error('Failed to update system:', error);
      return false;
    }
  },

  removeSystem: async (systemRowId) => {
    const { currentMap, systems, connections, signatures } = get();
    if (!currentMap) return false;

    // Store original state for rollback
    const originalSystems = systems;
    const originalConnections = connections;
    const originalSignatures = signatures;

    // Optimistic update - remove immediately from UI
    set((state) => ({
      systems: state.systems.filter((s) => s.id !== systemRowId),
      connections: state.connections.filter(
        (c) => c.sourceSystemId !== systemRowId && c.targetSystemId !== systemRowId
      ),
      signatures: state.signatures.filter((s) => s.systemId !== systemRowId),
      selectedSystemId: state.selectedSystemId === systemRowId ? null : state.selectedSystemId,
    }));

    try {
      const response = await fetch(apiUrl(`/api/maps/${currentMap.id}/systems/${systemRowId}`), {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to remove system');
      return true;
    } catch (error) {
      console.error('Failed to remove system:', error);
      // Rollback on failure
      set({
        systems: originalSystems,
        connections: originalConnections,
        signatures: originalSignatures,
      });
      return false;
    }
  },

  // Connection actions
  addConnection: async (sourceSystemId, targetSystemId, wormholeType, massStatus = 'fresh', timeStatus = 'stable') => {
    const { currentMap } = get();
    if (!currentMap) return null;

    try {
      const response = await fetch(apiUrl(`/api/maps/${currentMap.id}/connections`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceSystemId, targetSystemId, wormholeType, massStatus, timeStatus }),
        credentials: 'include',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create connection');
      }
      const data = await response.json();
      set((state) => ({ connections: [...state.connections, data.connection] }));
      return data.connection;
    } catch (error) {
      console.error('Failed to create connection:', error);
      return null;
    }
  },

  updateConnection: async (connectionId, updates) => {
    const { currentMap } = get();
    if (!currentMap) return false;

    try {
      const response = await fetch(apiUrl(`/api/maps/${currentMap.id}/connections/${connectionId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to update connection');

      set((state) => ({
        connections: state.connections.map((c) =>
          c.id === connectionId ? { ...c, ...updates } : c
        ),
      }));
      return true;
    } catch (error) {
      console.error('Failed to update connection:', error);
      return false;
    }
  },

  removeConnection: async (connectionId) => {
    const { currentMap, connections } = get();
    if (!currentMap) return false;

    // Store original state for rollback
    const originalConnections = connections;

    // Optimistic update - remove immediately from UI
    set((state) => ({
      connections: state.connections.filter((c) => c.id !== connectionId),
      selectedConnectionId: state.selectedConnectionId === connectionId ? null : state.selectedConnectionId,
    }));

    try {
      const response = await fetch(apiUrl(`/api/maps/${currentMap.id}/connections/${connectionId}`), {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to remove connection');
      return true;
    } catch (error) {
      console.error('Failed to remove connection:', error);
      // Rollback on failure
      set({ connections: originalConnections });
      return false;
    }
  },

  // Signature actions
  importSignatures: async (systemId, rawText, lazyDelete = false) => {
    const { currentMap } = get();
    if (!currentMap) return null;

    try {
      const response = await fetch(apiUrl(`/api/maps/${currentMap.id}/systems/${systemId}/signatures/import`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rawText, lazyDelete }),
        credentials: 'include',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to import signatures');
      }
      const data = await response.json();

      // Reload map to get updated signatures
      await get().loadMap(currentMap.id);

      return data.result;
    } catch (error) {
      console.error('Failed to import signatures:', error);
      return null;
    }
  },

  updateSignature: async (signatureId, updates) => {
    const { currentMap } = get();
    if (!currentMap) return false;

    try {
      const response = await fetch(apiUrl(`/api/maps/${currentMap.id}/signatures/${signatureId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to update signature');

      set((state) => ({
        signatures: state.signatures.map((s) =>
          s.id === signatureId ? { ...s, ...updates } : s
        ),
      }));
      return true;
    } catch (error) {
      console.error('Failed to update signature:', error);
      return false;
    }
  },

  removeSignature: async (signatureId) => {
    const { currentMap } = get();
    if (!currentMap) return false;

    try {
      const response = await fetch(apiUrl(`/api/maps/${currentMap.id}/signatures/${signatureId}`), {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to remove signature');

      set((state) => ({
        signatures: state.signatures.filter((s) => s.id !== signatureId),
      }));
      return true;
    } catch (error) {
      console.error('Failed to remove signature:', error);
      return false;
    }
  },

  // Selection actions
  selectSystem: (systemId) => {
    set({ selectedSystemId: systemId, selectedConnectionId: null });
  },

  selectConnection: (connectionId) => {
    set({ selectedConnectionId: connectionId, selectedSystemId: null });
  },

  // Optimistic update actions (for WebSocket sync)
  applySystemUpdate: (system) => {
    set((state) => {
      const exists = state.systems.some((s) => s.id === system.id);
      if (exists) {
        return { systems: state.systems.map((s) => (s.id === system.id ? system : s)) };
      }
      return { systems: [...state.systems, system] };
    });
  },

  applyConnectionUpdate: (connection) => {
    set((state) => {
      const exists = state.connections.some((c) => c.id === connection.id);
      if (exists) {
        return { connections: state.connections.map((c) => (c.id === connection.id ? connection : c)) };
      }
      return { connections: [...state.connections, connection] };
    });
  },

  applySignatureUpdate: (signature) => {
    set((state) => {
      const exists = state.signatures.some((s) => s.id === signature.id);
      if (exists) {
        return { signatures: state.signatures.map((s) => (s.id === signature.id ? signature : s)) };
      }
      return { signatures: [...state.signatures, signature] };
    });
  },

  applySystemRemoval: (systemId) => {
    set((state) => ({
      systems: state.systems.filter((s) => s.id !== systemId),
      connections: state.connections.filter(
        (c) => c.sourceSystemId !== systemId && c.targetSystemId !== systemId
      ),
      selectedSystemId: state.selectedSystemId === systemId ? null : state.selectedSystemId,
    }));
  },

  applyConnectionRemoval: (connectionId) => {
    set((state) => ({
      connections: state.connections.filter((c) => c.id !== connectionId),
      selectedConnectionId: state.selectedConnectionId === connectionId ? null : state.selectedConnectionId,
    }));
  },

  applySignatureRemoval: (signatureId) => {
    set((state) => ({
      signatures: state.signatures.filter((s) => s.id !== signatureId),
    }));
  },

  // WebSocket sync actions
  setMapState: (payload) => {
    set((state) => ({
      systems: (payload.systems as SystemData[]) || state.systems,
      connections: (payload.connections as ConnectionData[]) || state.connections,
      signatures: (payload.signatures as SignatureData[]) || state.signatures,
      presence: payload.presence || state.presence,
    }));
  },

  applyUpdate: (action, data) => {
    const state = get();
    switch (action) {
      case 'ADD_SYSTEM':
      case 'UPDATE_SYSTEM':
        state.applySystemUpdate(data as SystemData);
        break;
      case 'REMOVE_SYSTEM':
        state.applySystemRemoval((data as { id: string }).id);
        break;
      case 'ADD_CONNECTION':
      case 'UPDATE_CONNECTION':
        state.applyConnectionUpdate(data as ConnectionData);
        break;
      case 'REMOVE_CONNECTION':
        state.applyConnectionRemoval((data as { id: string }).id);
        break;
      case 'ADD_SIGNATURE':
      case 'UPDATE_SIGNATURE':
        state.applySignatureUpdate(data as SignatureData);
        break;
      case 'REMOVE_SIGNATURE':
        state.applySignatureRemoval((data as { id: string }).id);
        break;
      default:
        console.warn('Unknown WebSocket action:', action);
    }
  },

  confirmUpdate: (action, data) => {
    // For now, confirmUpdate behaves the same as applyUpdate
    // In future, this could clear optimistic update flags
    get().applyUpdate(action, data);
  },

  setPresence: (presence) => {
    set({ presence });
  },
}));
